// SCR-STAFF-501 — پرونده بالینی (US-CLN-001 تا 006، 031، 090): سربرگ هشدار، آلرژی‌ها (NKA صریح، تغییر وضعیت با دلیل؛ بدون حذف)، علائم حیاتی
// (BMI، نشان غیرطبیعی، تأیید مقدار غیرفیزیولوژیک)، مشکلات، داروها، سوابق، وضعیت‌های ویژه با اعتبار، هشدار دستی، مواجهات و خط زمانی با فیلتر نوع.
// درون تب «پرونده بالینی» صفحه مراجعه‌کننده وقتی سیاست دید اجازه دهد (ClinicalRecordPanel).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type ClnAllergyCategory,
  type ClnAllergySeverity,
  type ClnAllergyVerification,
  type ClnAllergyView,
  type ClnClinicalStatusKind,
  type ClnClinicalStatusView,
  type ClnHistorySection,
  type ClnMedicationView,
  type ClnProblemView,
  type ClnVitalSignView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, FormDialog, ReasonDialog, StatusChip, toJalali, toJalaliDateTime, useDisplaySettings, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { ClinicalAlertsBar, clnKeys, encounterPath, errorCode, num, remindersPath, SCR_STAFF_501, useClinicalAlerts } from './common';
import { AdverseEventsPanel } from './AdverseEvents';
import { BeautyProfilePanel } from './BeautyProfile';
import { ToothChartPanel } from './ToothChart';
import { ClinicalImagesPanel } from './ClinicalImages';
import { TreatmentPlansPanel } from './TreatmentPlans';
import { ExportSelectionDialog, OutcomesPanel, RiskPanel, SensitiveBadge, TrendsPanel } from './PsychPanels';

const CATEGORIES: readonly ClnAllergyCategory[] = ['Drug', 'Food', 'Environmental', 'Cosmetic', 'MedicalMaterial'];
const SEVERITIES: readonly ClnAllergySeverity[] = ['Mild', 'Moderate', 'Severe', 'Anaphylaxis'];
const VERIFICATIONS: readonly ClnAllergyVerification[] = ['Reported', 'Confirmed'];
const SECTIONS: readonly ClnHistorySection[] = ['PastIllness', 'Surgical', 'Family', 'Social'];
const HISTORY_STATUS_SECTIONS = ['PastIllness', 'Surgical', 'Family', 'Social', 'Medications'] as const;
const STOP_REASONS = ['Unspecified', 'CourseCompleted', 'Ineffective', 'AdverseEffect', 'PatientChoice', 'Contraindication', 'PrescriberDecision', 'Other'] as const;
const ADVERSE_SEVERITIES = ['Mild', 'Moderate', 'Severe'] as const;
const STATUS_KINDS: readonly ClnClinicalStatusKind[] = ['Pregnant', 'Breastfeeding', 'Immunocompromised', 'ActiveInfection', 'RecentSunExposure', 'KeloidTendency', 'Other'];
const VITAL_FIELDS = ['systolic', 'diastolic', 'pulse', 'temperature', 'respiratoryRate', 'oxygenSaturation', 'heightCm', 'weightKg', 'waistCm', 'bloodGlucose', 'pain'] as const;
type VitalField = (typeof VITAL_FIELDS)[number];
const TIMELINE_TYPES = ['encounter', 'diagnosis', 'status', 'allergy', 'problem'] as const;
const TIMELINE_TABS = ['all', 'current', 'milestones', 'mine'] as const;
type TimelineTab = (typeof TIMELINE_TABS)[number];

type Dialog = 'allergy' | 'vitals' | 'problem' | 'medication' | 'history' | 'status' | 'manual' | 'stopMed' | 'milestone' | null;

interface HistorySectionStatusView {
  section: string;
  status: 'NotReviewed' | 'NoneKnown' | 'HasEntries';
  source: string;
  recordedAt: string;
  recordedByName: string | null;
}

interface ClinicalReminderView {
  id: string;
  status: 'Open' | 'Due' | 'Done' | 'Cancelled';
  dueOn: string;
  internalText: string;
}

interface MilestoneView {
  id: string;
  itemType: string;
  itemId: string;
  label: string | null;
  markedAt: string;
  markedByName: string | null;
}

// پاسخ `:stop` همان MedicationView است با دو فیلد افزایشی پیشنهاد آلرژی (US-CLN-151).
type MedicationStopResult = ClnMedicationView & { suggestAllergy?: boolean; suggestedAgent?: string | null };

type TimelineItem = {
  type: string;
  id: string;
  occurredAt: string;
  titleKey: string;
  summary: string | null;
  encounterId: string | null;
  authorDisplayName?: string | null;
  otherProvider?: boolean;
};

type MedicationRow = ClnMedicationView & { otherProvider?: boolean; recordedByName?: string | null };

function Section({ title, action, children, testId }: { title: string; action?: React.ReactNode; children: React.ReactNode; testId: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }} data-testid={testId}>
      <Stack direction="row" sx={{ alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" component="h3" sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

export function ClinicalRecord({ patientId }: { patientId: string }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const { timeZone } = useDisplaySettings();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const tenantId = session?.tenantId;
  const full = access.accessMode === 'Full';
  const canHistory = full && access.can('cln.history.manage');
  const canVitals = full && access.can('cln.vitals.record');
  const [tab, setTab] = useState<'summary' | 'encounters' | 'timeline' | 'plans' | 'beauty' | 'dental' | 'images' | 'adverse' | 'charts' | 'risk'>('summary');
  // AP-CLN-03: نمودارها (SCR-STAFF-509)، ارزیابی خطر (HighlySensitive) و انتخاب یادداشت حساس برای خروجی.
  const canSensitive = access.can('cln.record.view_sensitive');
  const canRisk = access.can('cln.risk.assess') || canSensitive;
  const [exporting, setExporting] = useState(false);
  const canImages = access.can('cln.image.view');
  const [dialog, setDialog] = useState<Dialog>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [confirmImplausible, setConfirmImplausible] = useState(false);
  const [ending, setEnding] = useState<{ kind: 'allergy' | 'status'; id: string } | null>(null);
  const [timelineTypes, setTimelineTypes] = useState<string[]>([]);
  const [timelineTab, setTimelineTab] = useState<TimelineTab>('all');
  const [stoppingId, setStoppingId] = useState<string | null>(null);
  const path = { params: { path: { patientId } } };
  const canReminders = full && access.can('cln.reminder.manage');

  const alerts = useClinicalAlerts(patientId);
  const summary = useQuery({ queryKey: clnKeys.summary(tenantId, patientId), queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/summary', path)) });
  const vitals = useQuery({ queryKey: clnKeys.section(tenantId, patientId, 'vitals'), queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/vital-signs', path)) });
  const history = useQuery({ queryKey: clnKeys.section(tenantId, patientId, 'history'), queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/history', path)) });
  const historyStatuses = useQuery({
    queryKey: clnKeys.historyStatuses(tenantId, patientId),
    queryFn: async () =>
      (await unwrap(
        tenantApi.GET('/api/v1/clinical/patients/{patientId}/history-section-statuses', path as never),
      )) as { items: HistorySectionStatusView[]; showUnreviewedBadge: boolean },
  });
  const reminders = useQuery({
    queryKey: clnKeys.reminders(tenantId, patientId),
    enabled: canReminders,
    queryFn: async () =>
      (await unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/reminders', path as never))) as ClinicalReminderView[],
  });
  const milestones = useQuery({
    queryKey: clnKeys.milestones(tenantId, patientId),
    enabled: tab === 'timeline' && timelineTab === 'milestones',
    queryFn: async () =>
      (await unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/milestones', path as never))) as MilestoneView[],
  });
  const encounters = useQuery({
    queryKey: clnKeys.section(tenantId, patientId, 'encounters'),
    enabled: tab === 'encounters',
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/encounters', path)),
  });
  const timeline = useQuery({
    queryKey: [...clnKeys.section(tenantId, patientId, 'timeline'), timelineTab, timelineTypes.join(',')],
    enabled: tab === 'timeline',
    queryFn: async () =>
      (await unwrap(
        tenantApi.GET('/api/v1/clinical/patients/{patientId}/timeline', {
          params: {
            path: { patientId },
            query: { types: timelineTypes.length ? timelineTypes.join(',') : undefined, tab: timelineTab, limit: 50 } as never,
          },
        }),
      )) as { items: TimelineItem[]; nextCursor: string | null },
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: clnKeys.patient(tenantId, patientId) });
  const set = (field: string, value: string) => setForm((current) => ({ ...current, [field]: value }));
  const open = (next: Dialog, initial: Record<string, string> = {}) => {
    setForm(initial);
    setConfirmImplausible(false);
    setDialog(next);
  };
  const text = (field: string) => (form[field] ?? '').trim() || null;
  const number = (field: string) => (text(field) === null ? null : Number(text(field)));

  const save = useMutation({
    mutationFn: async () => {
      const headers = { 'Idempotency-Key': key.current() };
      switch (dialog) {
        case 'allergy':
          return unwrap(
            tenantApi.POST('/api/v1/clinical/patients/{patientId}/allergies', {
              ...path,
              headers,
              body: {
                category: (form.category ?? 'Drug') as ClnAllergyCategory,
                agentCode: null,
                agent: text('agent') ?? '',
                reaction: text('reaction'),
                severity: (form.severity ?? 'Moderate') as ClnAllergySeverity,
                verification: (form.verification ?? 'Reported') as ClnAllergyVerification,
                onsetDate: text('onsetDate'),
              },
            }),
          );
        case 'vitals': {
          const body = Object.fromEntries(VITAL_FIELDS.map((field) => [field, number(field)])) as Record<VitalField, number | null>;
          return unwrap(
            tenantApi.POST('/api/v1/clinical/patients/{patientId}/vital-signs', { ...path, headers, body: { ...body, measuredAt: null, encounterId: null, confirmImplausible } }),
          );
        }
        case 'problem':
          return unwrap(
            tenantApi.POST('/api/v1/clinical/patients/{patientId}/problems', {
              ...path,
              headers,
              body: { title: text('title') ?? '', code: text('code'), onsetDate: text('onsetDate'), isHighRisk: form.isHighRisk === 'true', note: text('note'), encounterId: null, diagnosisId: null },
            }),
          );
        case 'medication':
          return unwrap(
            tenantApi.POST('/api/v1/clinical/patients/{patientId}/medications', {
              ...path,
              headers,
              body: { drugCode: null, name: text('name') ?? '', drugClass: text('drugClass'), dose: text('dose'), frequency: text('frequency'), startDate: text('startDate'), source: null },
            }),
          );
        case 'history':
          return unwrap(
            tenantApi.POST('/api/v1/clinical/patients/{patientId}/history', {
              ...path,
              headers,
              body: { section: (form.section ?? 'PastIllness') as ClnHistorySection, title: text('title') ?? '', details: text('details'), entryDate: text('entryDate'), location: text('location') },
            }),
          );
        case 'status':
          return unwrap(
            tenantApi.POST('/api/v1/clinical/patients/{patientId}/clinical-statuses', {
              ...path,
              headers,
              body: { kind: (form.kind ?? 'Pregnant') as ClnClinicalStatusKind, text: text('text'), estimatedDueDate: text('estimatedDueDate'), validUntil: text('validUntil') },
            }),
          );
        case 'manual':
          return unwrap(
            tenantApi.POST('/api/v1/clinical/patients/{patientId}/manual-alerts', {
              ...path,
              body: { text: text('text') ?? '', severity: form.severity === 'Critical' ? 'Critical' : 'Warning' },
            }),
          );
        case 'stopMed':
          return unwrap(
            tenantApi.POST('/api/v1/clinical/patients/{patientId}/medications/{medicationId}:stop', {
              params: { path: { patientId, medicationId: stoppingId ?? '' } },
              body: {
                endDate: null,
                stopReasonCode: form.stopReasonCode || 'Unspecified',
                stopReasonText: text('stopReasonText'),
                adverseReaction: text('adverseReaction'),
                adverseSeverity: form.stopReasonCode === 'AdverseEffect' ? (form.adverseSeverity || null) : null,
              } as never,
            }),
          ).then((value) => value as unknown as MedicationStopResult);
        case 'milestone':
          return unwrap(
            tenantApi.POST('/api/v1/clinical/patients/{patientId}/milestones', {
              ...path,
              headers,
              body: { itemType: text('itemType') ?? 'encounter', itemId: text('itemId') ?? '', label: text('label') },
            }),
          );
        default:
          return null;
      }
    },
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (result) => {
      const stop = result as MedicationStopResult | null;
      if (dialog === 'stopMed' && stop?.suggestAllergy) {
        setStoppingId(null);
        setDialog(null);
        open('allergy', {
          category: 'Drug',
          severity: form.adverseSeverity || 'Moderate',
          verification: 'Reported',
          agent: stop.suggestedAgent ?? '',
          reaction: form.adverseReaction ?? '',
        });
        refresh();
        return;
      }
      setStoppingId(null);
      setDialog(null);
      refresh();
      void queryClient.invalidateQueries({ queryKey: clnKeys.historyStatuses(tenantId, patientId) });
      void queryClient.invalidateQueries({ queryKey: clnKeys.milestones(tenantId, patientId) });
    },
    onError: (error) => {
      if (errorCode(error) === 'PHX-CLN-0102') {
        setConfirmImplausible(true);
      }
    },
  });
  const noKnown = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/clinical/patients/{patientId}/allergies:no-known', path)),
    onSuccess: refresh,
  });
  const markNone = useMutation({
    mutationFn: (section: string) =>
      unwrap(
        tenantApi.POST('/api/v1/clinical/patients/{patientId}/history-section-statuses:none', {
          ...path,
          body: { section },
        }),
      ),
    onSuccess: () => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: clnKeys.historyStatuses(tenantId, patientId) });
    },
  });
  const deleteMilestone = useMutation({
    mutationFn: (milestoneId: string) =>
      unwrap(
        tenantApi.DELETE('/api/v1/clinical/patients/{patientId}/milestones/{milestoneId}', {
          params: { path: { patientId, milestoneId } },
        }),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: clnKeys.milestones(tenantId, patientId) }),
  });
  const endItem = useMutation({
    mutationFn: async (reason: string) => {
      if (ending?.kind === 'allergy') {
        await unwrap(tenantApi.POST('/api/v1/clinical/patients/{patientId}/allergies/{allergyId}:change-status', { params: { path: { patientId, allergyId: ending.id } }, body: { status: 'Refuted', reason } }));
      } else {
        await unwrap(tenantApi.POST('/api/v1/clinical/patients/{patientId}/clinical-statuses/{statusId}:end', { params: { path: { patientId, statusId: ending?.id ?? '' } }, body: { reason } }));
      }
    },
    onSuccess: () => {
      setEnding(null);
      refresh();
    },
  });
  const confirmStatus = useMutation({
    mutationFn: (statusId: string) => unwrap(tenantApi.POST('/api/v1/clinical/patients/{patientId}/clinical-statuses/{statusId}:confirm', { params: { path: { patientId, statusId } } })),
    onSuccess: refresh,
  });
  const providerLabel = (name: string | null | undefined, otherProvider?: boolean) =>
    otherProvider ? t('cln:record.otherProvider') : (name ?? null);

  const date = (value: string | null | undefined) => (value ? toJalali(value, { timeZone }) : '—');
  const when = (value: string | null | undefined) => (value ? toJalaliDateTime(value, { timeZone }) : '—');
  const add = (dialogName: Dialog, enabled: boolean, initial: Record<string, string> = {}) =>
    enabled ? (
      <Button size="small" onClick={() => open(dialogName, initial)} data-testid={`cln-add-${dialogName ?? ''}`}>
        {t('cln:common.add')}
      </Button>
    ) : null;

  const allergyColumns: DataTableColumn<ClnAllergyView>[] = [
    { id: 'agent', headerKey: 'cln:allergy.agent', cell: (row) => row.agent },
    { id: 'category', headerKey: 'cln:allergy.category', cell: (row) => t(`cln:allergy_category.${row.category}`) },
    { id: 'reaction', headerKey: 'cln:allergy.reaction', cell: (row) => row.reaction ?? '—' },
    { id: 'severity', headerKey: 'cln:allergy.severity', cell: (row) => t(`cln:severity.${row.severity}`) },
    { id: 'verification', headerKey: 'cln:allergy.verification', cell: (row) => t(`cln:verification.${row.verification}`) },
    { id: 'status', headerKey: 'cln:common.status', cell: (row) => <StatusChip tone={row.status === 'Active' ? 'active' : 'inactive'} labelKey={`cln:allergy_status.${row.status}`} /> },
    {
      id: 'actions',
      headerKey: 'cln:common.actions',
      cell: (row) =>
        canHistory && row.status === 'Active' ? (
          <Button size="small" onClick={() => setEnding({ kind: 'allergy', id: row.id })} data-testid="cln-allergy-refute">
            {t('cln:allergy.refute')}
          </Button>
        ) : null,
    },
  ];
  const vitalColumns: DataTableColumn<ClnVitalSignView>[] = [
    { id: 'at', headerKey: 'cln:vitals.measured_at', cell: (row) => when(row.measuredAt) },
    {
      id: 'bp',
      headerKey: 'cln:vitals.bp',
      cell: (row) => (row.systolic !== null && row.diastolic !== null ? <bdi dir="ltr" style={{ color: row.abnormalFields.includes('Systolic') || row.abnormalFields.includes('Diastolic') ? '#dc2626' : undefined }}>{`${num(row.systolic)}/${num(row.diastolic)}`}</bdi> : '—'),
    },
    { id: 'pulse', headerKey: 'cln:vitals.pulse', cell: (row) => num(row.pulse) ?? '—' },
    { id: 'weight', headerKey: 'cln:vitals.weight', cell: (row) => num(row.weightKg) ?? '—' },
    { id: 'bmi', headerKey: 'cln:vitals.bmi', cell: (row) => num(row.bmi) ?? '—' },
    {
      id: 'flags',
      headerKey: 'cln:vitals.flags',
      cell: (row) => (row.abnormalFields.length ? <Chip size="small" color="warning" label={t('cln:vitals.abnormal', { count: row.abnormalFields.length })} data-testid="cln-vital-abnormal" /> : '—'),
    },
  ];
  const problemColumns: DataTableColumn<ClnProblemView>[] = [
    { id: 'title', headerKey: 'cln:problem.title_field', cell: (row) => row.title },
    { id: 'code', headerKey: 'cln:problem.code', cell: (row) => (row.code ? <bdi dir="ltr">{row.code}</bdi> : '—') },
    { id: 'status', headerKey: 'cln:common.status', cell: (row) => t(`cln:problem_status.${row.status}`) },
    { id: 'onset', headerKey: 'cln:problem.onset', cell: (row) => date(row.onsetDate) },
    { id: 'risk', headerKey: 'cln:problem.high_risk', cell: (row) => (row.isHighRisk ? <Chip size="small" color="error" label={t('cln:problem.high_risk')} /> : '—') },
  ];
  const medicationColumns: DataTableColumn<MedicationRow>[] = [
    { id: 'name', headerKey: 'cln:medication.name', cell: (row) => (row.isSensitive ? <strong data-testid="cln-medication-sensitive">{row.name}</strong> : row.name) },
    { id: 'class', headerKey: 'cln:medication.drug_class', cell: (row) => (row.drugClass ? t(`cln:drug_class.${row.drugClass}`) : '—') },
    { id: 'dose', headerKey: 'cln:medication.dose', cell: (row) => [row.dose, row.frequency].filter(Boolean).join(' · ') || '—' },
    { id: 'source', headerKey: 'cln:medication.source', cell: (row) => t(`cln:medication_source.${row.source}`) },
    {
      id: 'recordedBy',
      headerKey: 'cln:pending.author',
      cell: (row) => providerLabel(row.recordedByName, row.otherProvider) ?? '—',
    },
    { id: 'status', headerKey: 'cln:common.status', cell: (row) => t(`cln:medication_status.${row.status}`) },
    {
      id: 'actions',
      headerKey: 'cln:common.actions',
      cell: (row) =>
        canHistory && row.status === 'Active' ? (
          <Button
            size="small"
            onClick={() => {
              setStoppingId(row.id);
              open('stopMed', { stopReasonCode: 'Unspecified', adverseSeverity: 'Moderate' });
            }}
            data-testid="cln-medication-stop"
          >
            {t('cln:medication.stop')}
          </Button>
        ) : null,
    },
  ];
  const statusBySection = Object.fromEntries((historyStatuses.data?.items ?? []).map((item) => [item.section, item]));
  const openReminders = (reminders.data ?? []).filter((row) => row.status === 'Open' || row.status === 'Due');
  const statusColumns: DataTableColumn<ClnClinicalStatusView>[] = [
    { id: 'kind', headerKey: 'cln:status.kind', cell: (row) => t(`cln:status_kind.${row.kind}`) + (row.text ? ` — ${row.text}` : '') },
    { id: 'valid', headerKey: 'cln:status.valid_until', cell: (row) => date(row.validUntil) },
    {
      id: 'state',
      headerKey: 'cln:common.status',
      cell: (row) =>
        row.state === 'Ended' ? (
          <StatusChip tone="inactive" labelKey="cln:status.ended" />
        ) : row.needsReview ? (
          <StatusChip tone="pending" labelKey="cln:status.needs_review" />
        ) : (
          <StatusChip tone="active" labelKey="cln:status.active" />
        ),
    },
    {
      id: 'actions',
      headerKey: 'cln:common.actions',
      cell: (row) =>
        canHistory && row.state === 'Active' ? (
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => confirmStatus.mutate(row.id)} data-testid="cln-status-confirm">
              {t('cln:status.confirm')}
            </Button>
            <Button size="small" color="inherit" onClick={() => setEnding({ kind: 'status', id: row.id })}>
              {t('cln:status.end')}
            </Button>
          </Stack>
        ) : null,
    },
  ];

  const data = summary.data;
  return (
    <Stack spacing={2} data-screen-id={SCR_STAFF_501} data-testid="cln-record">
      <Paper variant="outlined" sx={{ p: 1.5 }} data-screen-id="COMP-CLINICAL-ALERTS">
        <ClinicalAlertsBar data={alerts.data} />
      </Paper>
      <Tabs value={tab} onChange={(_event, value: typeof tab) => setTab(value)} aria-label={t('cln:record.title')}>
        <Tab value="summary" label={t('cln:record.tabs.summary')} />
        <Tab value="encounters" label={t('cln:record.tabs.encounters')} data-testid="cln-tab-encounters" />
        <Tab value="timeline" label={t('cln:record.tabs.timeline')} data-testid="cln-tab-timeline" />
        <Tab value="plans" label={t('cln:record.tabs.plans')} data-testid="cln-tab-plans" />
        <Tab value="beauty" label={t('cln:record.tabs.beauty')} data-testid="cln-tab-beauty" />
        <Tab value="dental" label={t('cln:record.tabs.dental')} data-testid="cln-tab-dental" />
        {canImages && <Tab value="images" label={t('cln:record.tabs.images')} data-testid="cln-tab-images" />}
        <Tab value="adverse" label={t('cln:record.tabs.adverse')} data-testid="cln-tab-adverse" />
        <Tab value="charts" label={t('cln:record.tabs.charts')} data-testid="cln-tab-charts" />
        {canRisk && <Tab value="risk" label={t('cln:record.tabs.risk')} data-testid="cln-tab-risk" />}
      </Tabs>
      {tab === 'charts' && (
        <Stack spacing={2} data-screen-id="SCR-STAFF-509">
          <TrendsPanel patientId={patientId} />
          {canSensitive && <OutcomesPanel patientId={patientId} />}
        </Stack>
      )}
      {tab === 'risk' && canRisk && <RiskPanel patientId={patientId} />}
      {exporting && <ExportSelectionDialog patientId={patientId} onClose={() => setExporting(false)} />}
      {tab === 'plans' && <TreatmentPlansPanel patientId={patientId} />}
      {tab === 'beauty' && <BeautyProfilePanel patientId={patientId} />}
      {tab === 'dental' && <ToothChartPanel patientId={patientId} />}
      {tab === 'images' && canImages && <ClinicalImagesPanel patientId={patientId} />}
      {tab === 'adverse' && <AdverseEventsPanel patientId={patientId} />}

      {tab === 'summary' && (
        <Stack spacing={2}>
          {canReminders ? (
            <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="cln-reminder-strip">
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <Typography variant="body2">{t('cln:reminders.strip', { count: openReminders.length })}</Typography>
                <Button component={RouterLink} to={remindersPath(patientId)} size="small" variant="outlined">
                  {t('cln:reminders.openPage')}
                </Button>
              </Stack>
            </Paper>
          ) : null}
          <Section
            title={t('cln:allergy.title')}
            testId="cln-allergies"
            action={
              <Stack direction="row" spacing={1}>
                {canHistory && (data?.allergies.length ?? 0) === 0 && (
                  <Button size="small" onClick={() => noKnown.mutate()} disabled={noKnown.isPending} data-testid="cln-nka">
                    {t('cln:allergy.nka')}
                  </Button>
                )}
                {add('allergy', canHistory, { category: 'Drug', severity: 'Moderate', verification: 'Reported' })}
              </Stack>
            }
          >
            {data?.allergyState === 'NoKnownAllergies' && (
              <Alert severity="success" sx={{ mb: 1 }} data-testid="cln-nka-recorded">
                {t('cln:allergy.nka_recorded', { time: when(data.nkaRecordedAt) })}
              </Alert>
            )}
            {data?.allergyState === 'Unreviewed' && (
              <Alert severity="warning" sx={{ mb: 1 }} data-testid="cln-allergy-unreviewed">
                {t('cln:alerts.allergy_unreviewed')}
              </Alert>
            )}
            <DataTable captionKey="cln:allergy.title" columns={allergyColumns} rows={data?.allergies} getRowId={(row) => row.id} isLoading={summary.isPending} error={summary.error} emptyTitleKey="cln:allergy.empty" />
          </Section>
          <Section title={t('cln:vitals.title')} testId="cln-vitals" action={add('vitals', canVitals)}>
            <DataTable captionKey="cln:vitals.title" columns={vitalColumns} rows={vitals.data?.items} getRowId={(row) => row.id} isLoading={vitals.isPending} error={vitals.error} emptyTitleKey="cln:vitals.empty" />
          </Section>
          <Section title={t('cln:problem.title')} testId="cln-problems" action={add('problem', canHistory)}>
            <DataTable captionKey="cln:problem.title" columns={problemColumns} rows={data?.problems} getRowId={(row) => row.id} isLoading={summary.isPending} error={summary.error} emptyTitleKey="cln:problem.empty" />
          </Section>
          <Section
            title={t('cln:medication.title')}
            testId="cln-medications"
            action={
              <Stack direction="row" spacing={1} alignItems="center">
                {historyStatuses.data?.showUnreviewedBadge && statusBySection.Medications?.status === 'NotReviewed' ? (
                  <Chip size="small" color="warning" label={t('cln:historySections.notReviewed')} data-testid="cln-med-not-reviewed" />
                ) : null}
                {canHistory && statusBySection.Medications?.status !== 'NoneKnown' && (data?.medications.filter((row) => row.status === 'Active').length ?? 0) === 0 ? (
                  <Button size="small" onClick={() => markNone.mutate('Medications')} disabled={markNone.isPending} data-testid="cln-med-none-known">
                    {t('cln:historySections.noneKnown')}
                  </Button>
                ) : null}
                {statusBySection.Medications?.status === 'NoneKnown' ? <Chip size="small" label={t('cln:historySections.noneKnown')} data-testid="cln-med-none-badge" /> : null}
                {add('medication', canHistory)}
              </Stack>
            }
          >
            <DataTable captionKey="cln:medication.title" columns={medicationColumns} rows={data?.medications as MedicationRow[] | undefined} getRowId={(row) => row.id} isLoading={summary.isPending} error={summary.error} emptyTitleKey="cln:medication.empty" />
          </Section>
          <Section title={t('cln:status.title')} testId="cln-statuses" action={add('status', canHistory, { kind: 'Pregnant' })}>
            <DataTable captionKey="cln:status.title" columns={statusColumns} rows={data?.statuses} getRowId={(row) => row.id} isLoading={summary.isPending} error={summary.error} emptyTitleKey="cln:status.empty" />
          </Section>
          <Section title={t('cln:history.title')} testId="cln-history" action={add('history', canHistory, { section: 'PastIllness' })}>
            <Stack spacing={1.5} sx={{ mb: 1.5 }} data-testid="cln-history-sections">
              {HISTORY_STATUS_SECTIONS.filter((section) => section !== 'Medications').map((section) => {
                const status = statusBySection[section];
                return (
                  <Stack key={section} direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                    <Typography variant="subtitle2">{t(`cln:history_section.${section}`)}</Typography>
                    {historyStatuses.data?.showUnreviewedBadge && status?.status === 'NotReviewed' ? (
                      <Chip size="small" color="warning" label={t('cln:historySections.notReviewed')} data-testid={`cln-history-badge-${section}`} />
                    ) : null}
                    {status?.status === 'NoneKnown' ? <Chip size="small" label={t('cln:historySections.noneKnown')} data-testid={`cln-history-none-${section}`} /> : null}
                    {status?.status === 'HasEntries' ? <Chip size="small" variant="outlined" label={t('cln:historySections.hasEntries')} /> : null}
                    {canHistory && status?.status !== 'NoneKnown' && status?.status !== 'HasEntries' ? (
                      <Button size="small" onClick={() => markNone.mutate(section)} disabled={markNone.isPending} data-testid={`cln-history-mark-none-${section}`}>
                        {t('cln:historySections.markNone')}
                      </Button>
                    ) : null}
                  </Stack>
                );
              })}
            </Stack>
            {(history.data ?? []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('cln:history.empty')}
              </Typography>
            ) : (
              (history.data ?? []).map((entry) => (
                <Typography key={entry.id} variant="body2">
                  <strong>{t(`cln:history_section.${entry.section}`)}</strong> · {entry.title}
                  {entry.details ? ` — ${entry.details}` : ''}
                  {entry.fromForm ? ` · ${t('cln:history.from_form')}` : ''}
                  {entry.recordedByName || (entry as { otherProvider?: boolean }).otherProvider
                    ? ` · ${providerLabel(entry.recordedByName, (entry as { otherProvider?: boolean }).otherProvider) ?? ''}`
                    : ''}
                </Typography>
              ))
            )}
          </Section>
          <Section title={t('cln:manual.title')} testId="cln-manual" action={add('manual', canHistory, { severity: 'Warning' })}>
            {(data?.manualAlerts ?? []).filter((alert) => alert.isActive).map((alert) => (
              <Typography key={alert.id} variant="body2">
                {alert.text}
              </Typography>
            ))}
          </Section>
        </Stack>
      )}

      {tab === 'encounters' && (
        <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="cln-encounters">
          {access.can('cln.record.view') && (
            <Button size="small" sx={{ mb: 1 }} onClick={() => setExporting(true)} data-testid="cln-export-selection">
              {t('cln:psych.export_selection')}
            </Button>
          )}
          {(encounters.data?.items ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {encounters.isPending ? '…' : t('cln:record.no_encounters')}
            </Typography>
          ) : (
            (encounters.data?.items ?? []).map((item) => (
              <Typography key={item.id} variant="body2" data-testid="cln-encounter-item" data-counseling={item.counselingSession}>
                <Link component={RouterLink} to={encounterPath(item.id)}>
                  {when(item.startedAt)} · {item.counselingSession ? t('cln:psych.counseling_session') : t(`cln:encounter_type.${item.type}`)}
                </Link>
                {item.counselingSession ? <> <SensitiveBadge /></> : null}
                {' · '}
                {t(`cln:encounter_status.${item.status}`)}
                {item.noteStatus ? ` · ${t(`cln:note_status.${item.noteStatus}`)}` : ''}
                {item.diagnoses.length ? ` · ${item.diagnoses.join(t('cln:common.list_separator'))}` : ''}
              </Typography>
            ))
          )}
        </Paper>
      )}

      {tab === 'timeline' && (
        <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="cln-timeline">
          <Tabs
            value={timelineTab}
            onChange={(_event, value: TimelineTab) => setTimelineTab(value)}
            aria-label={t('cln:record.tabs.timeline')}
            sx={{ mb: 1.5 }}
          >
            {TIMELINE_TABS.map((value) => (
              <Tab key={value} value={value} label={t(`cln:timelineTabs.${value}`)} data-testid={`cln-timeline-tab-${value}`} />
            ))}
          </Tabs>
          {timelineTab === 'milestones' ? (
            <Stack spacing={1} sx={{ mb: 1.5 }}>
              {canHistory ? (
                <Button size="small" onClick={() => open('milestone', { itemType: 'encounter' })} data-testid="cln-milestone-add">
                  {t('cln:milestones.add')}
                </Button>
              ) : null}
              {(milestones.data ?? []).length === 0 && !milestones.isPending ? (
                <Typography variant="body2" color="text.secondary">
                  {t('cln:milestones.empty')}
                </Typography>
              ) : (
                (milestones.data ?? []).map((item) => (
                  <Stack key={item.id} direction="row" spacing={1} alignItems="center" data-testid="cln-milestone-item">
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {when(item.markedAt)} · {item.label || item.itemType}
                      {item.markedByName ? ` · ${item.markedByName}` : ''}
                    </Typography>
                    {canHistory ? (
                      <Button size="small" color="inherit" onClick={() => deleteMilestone.mutate(item.id)}>
                        {t('cln:milestones.delete')}
                      </Button>
                    ) : null}
                  </Stack>
                ))
              )}
            </Stack>
          ) : (
            <>
              <ToggleButtonGroup
                size="small"
                value={timelineTypes}
                onChange={(_event, value: string[]) => setTimelineTypes(value)}
                aria-label={t('cln:timeline.filter')}
                sx={{ mb: 1.5, flexWrap: 'wrap' }}
              >
                {TIMELINE_TYPES.map((type) => (
                  <ToggleButton key={type} value={type} data-testid={`cln-timeline-type-${type}`}>
                    {t(`cln:timeline.types.${type}`)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {(timeline.data?.items ?? []).map((item) => (
                <Typography
                  key={`${item.type}-${item.id}`}
                  variant="body2"
                  data-testid="cln-timeline-item"
                  data-type={item.type}
                  data-other-provider={item.otherProvider ? 'true' : undefined}
                >
                  {when(item.occurredAt)} · {t(item.titleKey, { defaultValue: t(`cln:timeline.types.${item.type}`) })}
                  {item.summary ? ` — ${item.summary}` : ''}
                  {item.otherProvider || item.authorDisplayName
                    ? ` · ${providerLabel(item.authorDisplayName, item.otherProvider) ?? ''}`
                    : ''}
                  {item.encounterId ? (
                    <>
                      {' · '}
                      <Link component={RouterLink} to={encounterPath(item.encounterId)}>
                        {t('cln:timeline.open')}
                      </Link>
                    </>
                  ) : null}
                </Typography>
              ))}
              {!timeline.isPending && (timeline.data?.items ?? []).length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t('cln:timeline.empty')}
                </Typography>
              )}
            </>
          )}
        </Paper>
      )}

      <FormDialog
        open={dialog !== null}
        titleKey={
          dialog === 'stopMed' ? 'cln:medicationStop.title' : dialog === 'milestone' ? 'cln:milestones.add' : `cln:dialogs.${dialog ?? 'allergy'}`
        }
        onSubmit={() => save.mutate()}
        onClose={() => {
          setStoppingId(null);
          setDialog(null);
        }}
        isDirty={Object.values(form).some((value) => value !== '')}
        isSubmitting={save.isPending}
        submitError={errorCode(save.error) === 'PHX-CLN-0102' ? undefined : save.error}
      >
        {dialog === 'allergy' && (
          <>
            <TextField select label={t('cln:allergy.category')} value={form.category ?? 'Drug'} onChange={(event) => set('category', event.target.value)}>
              {CATEGORIES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`cln:allergy_category.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField label={t('cln:allergy.agent')} required value={form.agent ?? ''} onChange={(event) => set('agent', event.target.value)} slotProps={{ htmlInput: { maxLength: 200, 'data-testid': 'cln-allergy-agent' } }} />
            <TextField label={t('cln:allergy.reaction')} value={form.reaction ?? ''} onChange={(event) => set('reaction', event.target.value)} />
            <TextField select label={t('cln:allergy.severity')} value={form.severity ?? 'Moderate'} onChange={(event) => set('severity', event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'cln-allergy-severity' } }}>
              {SEVERITIES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`cln:severity.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField select label={t('cln:allergy.verification')} value={form.verification ?? 'Reported'} onChange={(event) => set('verification', event.target.value)}>
              {VERIFICATIONS.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`cln:verification.${value}`)}
                </MenuItem>
              ))}
            </TextField>
          </>
        )}
        {dialog === 'vitals' && (
          <>
            {confirmImplausible && (
              <Alert severity="warning" data-testid="cln-vitals-implausible">
                {t('cln:vitals.implausible')}
              </Alert>
            )}
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {VITAL_FIELDS.map((field) => (
                <TextField
                  key={field}
                  label={t(`cln:vitals.fields.${field}`)}
                  value={form[field] ?? ''}
                  onChange={(event) => set(field, event.target.value)}
                  sx={{ width: 150 }}
                  slotProps={{ htmlInput: { inputMode: 'decimal', dir: 'ltr', 'data-testid': `cln-vital-${field}` } }}
                />
              ))}
            </Stack>
            {confirmImplausible && (
              <FormControlLabel control={<Checkbox checked readOnly />} label={t('cln:vitals.confirm_implausible')} />
            )}
          </>
        )}
        {dialog === 'problem' && (
          <>
            <TextField label={t('cln:problem.title_field')} required value={form.title ?? ''} onChange={(event) => set('title', event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'cln-problem-title' } }} />
            <TextField label={t('cln:problem.code')} value={form.code ?? ''} onChange={(event) => set('code', event.target.value)} slotProps={{ htmlInput: { dir: 'ltr' } }} />
            <FormControlLabel control={<Checkbox checked={form.isHighRisk === 'true'} onChange={(event) => set('isHighRisk', String(event.target.checked))} />} label={t('cln:problem.high_risk')} />
          </>
        )}
        {dialog === 'medication' && (
          <>
            <TextField label={t('cln:medication.name')} required value={form.name ?? ''} onChange={(event) => set('name', event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'cln-medication-name' } }} />
            <TextField label={t('cln:medication.drug_class')} value={form.drugClass ?? ''} onChange={(event) => set('drugClass', event.target.value)} helperText={t('cln:medication.drug_class_hint')} slotProps={{ htmlInput: { dir: 'ltr' } }} />
            <TextField label={t('cln:medication.dose')} value={form.dose ?? ''} onChange={(event) => set('dose', event.target.value)} />
            <TextField label={t('cln:medication.frequency')} value={form.frequency ?? ''} onChange={(event) => set('frequency', event.target.value)} />
          </>
        )}
        {dialog === 'history' && (
          <>
            <TextField select label={t('cln:history.section')} value={form.section ?? 'PastIllness'} onChange={(event) => set('section', event.target.value)}>
              {SECTIONS.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`cln:history_section.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField label={t('cln:history.entry_title')} required value={form.title ?? ''} onChange={(event) => set('title', event.target.value)} />
            <TextField label={t('cln:history.details')} multiline minRows={2} value={form.details ?? ''} onChange={(event) => set('details', event.target.value)} />
          </>
        )}
        {dialog === 'status' && (
          <>
            <TextField select label={t('cln:status.kind')} value={form.kind ?? 'Pregnant'} onChange={(event) => set('kind', event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'cln-status-kind' } }}>
              {STATUS_KINDS.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`cln:status_kind.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField label={t('cln:status.text')} value={form.text ?? ''} onChange={(event) => set('text', event.target.value)} />
          </>
        )}
        {dialog === 'manual' && (
          <>
            <TextField label={t('cln:manual.text')} required value={form.text ?? ''} onChange={(event) => set('text', event.target.value)} slotProps={{ htmlInput: { maxLength: 300 } }} />
            <TextField select label={t('cln:manual.severity')} value={form.severity ?? 'Warning'} onChange={(event) => set('severity', event.target.value)}>
              <MenuItem value="Warning">{t('cln:manual.warning')}</MenuItem>
              <MenuItem value="Critical">{t('cln:manual.critical')}</MenuItem>
            </TextField>
          </>
        )}
        {dialog === 'stopMed' && (
          <>
            <TextField
              select
              label={t('cln:medicationStop.reason')}
              value={form.stopReasonCode ?? 'Unspecified'}
              onChange={(event) => set('stopReasonCode', event.target.value)}
              data-testid="cln-stop-reason"
            >
              {STOP_REASONS.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`cln:medicationStop.reasons.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField label={t('cln:medicationStop.reasonText')} value={form.stopReasonText ?? ''} onChange={(event) => set('stopReasonText', event.target.value)} />
            {form.stopReasonCode === 'AdverseEffect' ? (
              <>
                <Alert severity="info">{t('cln:medicationStop.suggestAllergy')}</Alert>
                <TextField
                  label={t('cln:medicationStop.adverse')}
                  required
                  value={form.adverseReaction ?? ''}
                  onChange={(event) => set('adverseReaction', event.target.value)}
                  slotProps={{ htmlInput: { 'data-testid': 'cln-stop-adverse' } }}
                />
                <TextField
                  select
                  label={t('cln:medicationStop.severity')}
                  value={form.adverseSeverity ?? 'Moderate'}
                  onChange={(event) => set('adverseSeverity', event.target.value)}
                >
                  {ADVERSE_SEVERITIES.map((value) => (
                    <MenuItem key={value} value={value}>
                      {t(`cln:severity.${value}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            ) : null}
          </>
        )}
        {dialog === 'milestone' && (
          <>
            <TextField label={t('cln:milestones.itemType')} required value={form.itemType ?? 'encounter'} onChange={(event) => set('itemType', event.target.value)} slotProps={{ htmlInput: { dir: 'ltr' } }} />
            <TextField label={t('cln:milestones.itemId')} required value={form.itemId ?? ''} onChange={(event) => set('itemId', event.target.value)} slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'cln-milestone-item-id' } }} />
            <TextField label={t('cln:milestones.label')} value={form.label ?? ''} onChange={(event) => set('label', event.target.value)} />
          </>
        )}
      </FormDialog>
      <ReasonDialog
        open={ending !== null}
        titleKey={ending?.kind === 'allergy' ? 'cln:allergy.refute' : 'cln:status.end'}
        messageKey={ending?.kind === 'allergy' ? 'cln:allergy.refute_message' : undefined}
        busy={endItem.isPending}
        error={endItem.error}
        onConfirm={(reason) => endItem.mutate(reason)}
        onCancel={() => setEnding(null)}
      />
    </Stack>
  );
}
